// @ts-check
// A dónde volver después de iniciar sesión.
//
// ## El problema que resuelve
//
// El profesor comparte con la clase una dirección concreta -- por ejemplo
// `index.html?ejercicio=area-rectangulo`. Un alumno la abre en su ordenador,
// no tiene sesión, la guarda de la página lo manda al login... y al entrar
// aterriza en `index.html` sin el ejercicio. El enlace pierde su sentido justo
// al final. Detectado por el usuario probando la aplicación con un alumno real
// (2026-08-06).
//
// Aquí se guarda el destino en el propio enlace del login y se recupera
// después de entrar.
//
// ## Por qué se valida, y por qué no basta con confiar
//
// Un parámetro de retorno sin validar es una **redirección abierta**: la vía
// clásica de phishing. `login.html?volver=https://sitio-falso.example` daría un
// enlace que empieza en TU dominio -- con tu aspecto y tu formulario de
// contraseña -- y termina en el de otro. Un alumno no distingue eso.
//
// Por eso el destino se comprueba con `security/safe-url.js#isSafeInternalUrl`
// antes de usarse: solo se acepta el MISMO origen. Cualquier otra cosa
// (`https://otro.example`, `//otro.example`, `javascript:…`) se descarta en
// silencio y se cae al destino por defecto.
import { isSafeInternalUrl } from '../security/safe-url.js';

/** Nombre del parámetro. En español, como el resto de la interfaz. */
export const PARAMETRO_RETORNO = 'volver';

/**
 * Construye la ruta del login conservando a dónde quería ir el alumno.
 *
 * Guarda solo `pathname + search` (nunca una URL absoluta): lo justo para
 * volver dentro de la aplicación, y nada que pueda apuntar fuera.
 *
 * @param {string} rutaLogin Página de login (p. ej. `'login.html'`).
 * @param {string} [destino] Destino a conservar. Por defecto, la página actual.
 * @returns {string}
 */
export function construirRutaLogin(rutaLogin, destino) {
  const actual =
    destino ??
    (typeof window === 'undefined' || !window.location
      ? null
      : `${window.location.pathname}${window.location.search}`);

  // Sin destino, o si es la propia portada, no se ensucia la URL: volver a
  // `index.html` es justo lo que el login hace por defecto.
  if (!actual || actual === '/' || actual.endsWith('/index.html')) return rutaLogin;

  return `${rutaLogin}?${PARAMETRO_RETORNO}=${encodeURIComponent(actual)}`;
}

/**
 * Lee el destino de retorno de la URL actual, ya validado.
 *
 * @param {string} [busqueda] Query string a leer. Por defecto, la actual.
 * @returns {string | null} El destino si es seguro; `null` si no lo hay o no
 *   pasa la validación (el llamador cae entonces a su ruta por defecto).
 */
export function leerRetornoSeguro(busqueda) {
  if (typeof window === 'undefined' || !window.location) return null;
  const query = busqueda ?? window.location.search;
  const destino = new URLSearchParams(query).get(PARAMETRO_RETORNO);
  if (!destino) return null;

  // La comprobación que impide la redirección abierta. `isSafeInternalUrl`
  // resuelve el valor contra el origen actual, así que también descarta las
  // formas que PARECEN relativas pero no lo son (`//otro.example/x`).
  if (!isSafeInternalUrl(destino)) return null;

  return destino;
}
